import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  listarClientesTodos,
  ativarClienteZenTag,
  editarClienteZenTag,
  obterClienteZenTagPorId,
} from "../services/api";

import { Search, Save } from "lucide-react";
import { useFeedbackAlert } from "./common/useFeedbackAlert";

const slugify = (text) =>
  (text || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const FormularioZenTag = ({ token }) => {
  const { showError, showSuccess, showWarning, FeedbackAlert } = useFeedbackAlert();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = id !== undefined;

  const [clientes, setClientes] = useState([]);
  const [clientesOriginais, setClientesOriginais] = useState([]);

  const [selectedClientId, setSelectedClientId] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingCliente, setLoadingCliente] = useState(false);
  const [error, setError] = useState(null);

  const [slug, setSlug] = useState("");
  const [descricao, setDescricao] = useState("");
  const [perguntaResponsavel, setPerguntaResponsavel] = useState(true);

  const [primeiroUsuario, setPrimeiroUsuario] = useState({
    nome: "",
    email: "",
    senha: "",
    telefone: "",
  });

  const [busca, setBusca] = useState("");

  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const all = await listarClientesTodos(token, {
          pageSize: 2000,
          sortBy: "razaosocial",
        });
        const arr = Array.isArray(all) ? all : [];
        setClientes(arr);
        setClientesOriginais(arr);
      } catch (err) {
        setError("Erro ao carregar clientes.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchClientes();
  }, [token]);

  useEffect(() => {
    if (!isEditMode) return;

    const fetchLicenca = async () => {
      setLoadingCliente(true);
      try {
        const resp = await obterClienteZenTagPorId(id, token);
        const lic = resp.data;

        setSelectedClientId(String(lic.clienteId ?? ""));
        setSlug(lic.slug ?? "");
        setDescricao(lic.descricao ?? "");
        setPerguntaResponsavel(
          lic.perguntaResponsavel === false || lic.PerguntaResponsavel === false
            ? false
            : true
        );

        setPrimeiroUsuario((p) => ({
          ...p,
          nome: "",
          email: "",
          senha: "",
          telefone: "",
        }));
      } catch (err) {
        setError("Erro ao carregar dados da licença ZenTag.");
        console.error(err);
      } finally {
        setLoadingCliente(false);
      }
    };

    fetchLicenca();
  }, [id, isEditMode, token]);

  const clienteSelecionado = useMemo(() => {
    const idNum = Number(selectedClientId);
    const base = Array.isArray(clientesOriginais) ? clientesOriginais : [];
    return base.find((c) => c.id === idNum) || null;
  }, [selectedClientId, clientesOriginais]);

  const handleBuscar = (e) => {
    const termo = e.target.value;
    setBusca(termo);

    const t = termo.toLowerCase();
    const base = Array.isArray(clientesOriginais) ? clientesOriginais : [];
    const filtered = base.filter(
      (c) =>
        (c.cnpj || "").toLowerCase().includes(t) ||
        (c.razaoSocial || "").toLowerCase().includes(t) ||
        (c.nomeFantasia || "").toLowerCase().includes(t)
    );
    setClientes(filtered);
  };

  useEffect(() => {
    if (isEditMode) return;
    if (!clienteSelecionado) return;

    const sugestaoSlug = slugify(
      clienteSelecionado.nomeFantasia || clienteSelecionado.razaoSocial
    );
    setSlug((prev) => prev || sugestaoSlug);

    setDescricao(
      (prev) =>
        prev ||
        `Licença ZenTag - ${
          clienteSelecionado.nomeFantasia || clienteSelecionado.razaoSocial
        }`
    );
  }, [clienteSelecionado, isEditMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isEditMode) {
      if (!descricao) {
        showWarning("Preencha a Descrição.");
        return;
      }

      setLoadingCliente(true);
      try {
        const payloadEdit = {
          slug: slugify(slug),
          descricao: descricao || "",
          perguntaResponsavel: Boolean(perguntaResponsavel),
        };

        await editarClienteZenTag(id, payloadEdit, token);
        showSuccess("ZenTag atualizado com sucesso!", () => navigate("/licenciamento/zentag/list"));
      } catch (err) {
        showError(err, "Erro ao atualizar ZenTag.");
        console.error(err);
      } finally {
        setLoadingCliente(false);
      }
      return;
    }

    if (!selectedClientId) {
      showWarning("Por favor, selecione um cliente.");
      return;
    }

    if (!primeiroUsuario.nome || !primeiroUsuario.email || !primeiroUsuario.senha) {
      showWarning("Preencha Nome, Email e Senha do primeiro usuário.");
      return;
    }

    setLoadingCliente(true);
    try {
      const revendaId = clienteSelecionado?.revendaId ?? 0;

      const payloadNovo = {
        clienteId: Number(selectedClientId),
        revendaId: revendaId,
        slug: slugify(slug),
        descricao: descricao || "",
        primeiroUsuarioNome: primeiroUsuario.nome,
        primeiroUsuarioEmail: primeiroUsuario.email,
        primeiroUsuarioSenha: primeiroUsuario.senha,
        primeiroUsuarioTelefone: primeiroUsuario.telefone || "",
      };

      await ativarClienteZenTag(payloadNovo, token);

      showSuccess("ZenTag ativado com sucesso!", () => navigate("/licenciamento/zentag/list"));
    } catch (err) {
      showError(err, "Erro ao ativar ZenTag.");
      console.error(err);
    } finally {
      setLoadingCliente(false);
    }
  };

  if (loading) return <div className="p-6 text-center text-gray-600 dark:text-slate-200">Carregando clientes...</div>;
  if (error) return <div className="p-6 text-center text-red-500">{error}</div>;

  return (
    <>
      <FeedbackAlert />
      <div className="max-w-4xl mx-auto">
      <div className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur">
        {/* header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
            {isEditMode ? "Editar ZenTag" : "Ativar ZenTag"}
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-300/80">
            {isEditMode
              ? "Altere a descrição e a opção de perguntar responsável. O slug não pode ser mudado."
              : "Selecione um cliente existente e informe os dados do primeiro usuário."}
          </p>
        </div>

        {/* seletor cliente */}
        <div className="zen-card rounded-2xl p-4 mb-6 dark:bg-white/5 dark:border-white/10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Buscar cliente por CNPJ / Razão / Nome Fantasia"
                value={busca}
                onChange={handleBuscar}
                disabled={isEditMode}
                className="
                  pl-10 pr-4 py-2.5 w-full rounded-xl border outline-none transition
                  zen-card text-gray-900 placeholder:text-gray-400
                  focus:ring-2 focus:ring-blue-500/60
                  disabled:opacity-60 disabled:cursor-not-allowed
                  dark:bg-slate-950/40 dark:border-white/10 dark:text-white dark:placeholder:text-slate-400
                  dark:focus:ring-blue-400/40
                "
              />
            </div>

            <select
              className="
                w-full p-2.5 rounded-xl border outline-none transition
                zen-card border-gray-300 text-gray-900
                focus:ring-2 focus:ring-blue-500/60
                disabled:opacity-60 disabled:cursor-not-allowed
                dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                dark:focus:ring-blue-400/40
              "
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              required
              disabled={isEditMode}
            >
              <option value="">-- Selecione um cliente --</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.razaoSocial} ({c.cnpj})
                </option>
              ))}
            </select>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* dados licença */}
          <div className="zen-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                  Slug
                </label>
                <input
                  value={slug}
                  onChange={(e) => setSlug(slugify(e.target.value))}
                  className="
                    w-full px-3 py-2.5 rounded-xl border outline-none transition
                    zen-card border-gray-300 text-gray-900
                    focus:ring-2 focus:ring-blue-500/60
                    dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                    dark:focus:ring-blue-400/40
                    disabled:opacity-70 disabled:cursor-not-allowed
                  "
                  placeholder="ex: mercado-do-ze"
                  required
                  disabled={isEditMode}
                  autoComplete="off"
                  spellCheck={false}
                />
                <p className="text-xs text-gray-500 dark:text-slate-300/70 mt-1">
                  {isEditMode
                    ? "Slug não pode ser alterado — define o banco do cliente."
                    : "Somente minúsculas, sem acento ou espaço."}
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                  Descrição
                </label>
                <input
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  className="
                    w-full px-3 py-2.5 rounded-xl border outline-none transition
                    zen-card border-gray-300 text-gray-900
                    focus:ring-2 focus:ring-blue-500/60
                    dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                    dark:focus:ring-blue-400/40
                  "
                  placeholder="ex: Unidade Morumbi"
                  required
                />
              </div>
            </div>
            {isEditMode && (
              <label className="mt-4 flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={perguntaResponsavel}
                  onChange={(e) => setPerguntaResponsavel(e.target.checked)}
                />
                <span>
                  <span className="block text-sm font-medium text-gray-700 dark:text-slate-200">
                    Perguntar responsável no lançamento
                  </span>
                  <span className="block text-xs text-gray-500 dark:text-slate-300/70 mt-0.5">
                    Quando desmarcado, o ZenTag usa o usuário logado e pula a pergunta de responsável.
                  </span>
                </span>
              </label>
            )}
          </div>

          {/* primeiro usuário */}
          {!isEditMode && (
            <div className="zen-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Primeiro usuário
                </h3>
                <span className="text-xs text-gray-500 dark:text-slate-300/70">
                  Será criado no ZenTag
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                    Nome
                  </label>
                  <input
                    value={primeiroUsuario.nome}
                    onChange={(e) =>
                      setPrimeiroUsuario((p) => ({ ...p, nome: e.target.value }))
                    }
                    className="
                      w-full px-3 py-2.5 rounded-xl border outline-none transition
                      zen-card border-gray-300 text-gray-900
                      focus:ring-2 focus:ring-blue-500/60
                      dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                      dark:focus:ring-blue-400/40
                    "
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                    Telefone
                  </label>
                  <input
                    value={primeiroUsuario.telefone}
                    onChange={(e) =>
                      setPrimeiroUsuario((p) => ({
                        ...p,
                        telefone: e.target.value,
                      }))
                    }
                    className="
                      w-full px-3 py-2.5 rounded-xl border outline-none transition
                      zen-card border-gray-300 text-gray-900
                      focus:ring-2 focus:ring-blue-500/60
                      dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                      dark:focus:ring-blue-400/40
                    "
                    placeholder="(11) 99999-9999"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={primeiroUsuario.email}
                    onChange={(e) =>
                      setPrimeiroUsuario((p) => ({ ...p, email: e.target.value }))
                    }
                    className="
                      w-full px-3 py-2.5 rounded-xl border outline-none transition
                      zen-card border-gray-300 text-gray-900
                      focus:ring-2 focus:ring-blue-500/60
                      dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                      dark:focus:ring-blue-400/40
                    "
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                    Senha
                  </label>
                  <input
                    type="password"
                    value={primeiroUsuario.senha}
                    onChange={(e) =>
                      setPrimeiroUsuario((p) => ({ ...p, senha: e.target.value }))
                    }
                    className="
                      w-full px-3 py-2.5 rounded-xl border outline-none transition
                      zen-card border-gray-300 text-gray-900
                      focus:ring-2 focus:ring-blue-500/60
                      dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                      dark:focus:ring-blue-400/40
                    "
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {/* actions */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loadingCliente}
              className="
                inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition
                bg-blue-600 text-white hover:bg-blue-500
                disabled:opacity-60 disabled:cursor-not-allowed
              "
            >
              <Save size={18} />
              {loadingCliente
                ? "Salvando..."
                : isEditMode
                ? "Salvar Alterações"
                : "Ativar ZenTag"}
            </button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default FormularioZenTag;
